import type { CSSProperties } from 'react';
import CheckBox from '@mui/material/Checkbox';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { mapToKey, searchByLabel } from '@witmetrics/utils/arrays';
import { useString } from '@/hooks/useString';
import UnderlineTextField from '@/components/inputs/UnderlineTextField';
import Button from '@/components/Button';
import Title from '@/components/Title';
import { classes, type FilterOption } from './utils';

export type FiltersListProps<N extends string, T> = {
  className?: string;
  style?: CSSProperties;
  name: N;
  title: string;
  filter?: boolean;
  selected: T[];
  options: FilterOption<T>[];
  onChange: (update: Record<N, T[]>) => void;
};

export default function FiltersList<
  N extends string,
  T extends string | number | boolean,
>({
  className,
  style = {},
  filter,
  title,
  name,
  options = [],
  selected = [],
  onChange,
}: FiltersListProps<N, T>) {
  const [search, setSearch] = useString();

  const toggleSelectAll = () => {
    let updates: T[];
    if (selected.length === options.length) updates = [];
    else updates = mapToKey(options, 'value');
    onChange({ [name]: updates } as Record<N, T[]>);
  };

  const handleSelectOption = (option: FilterOption<T>) => {
    let updates;
    if (selected.includes(option.value)) {
      updates = selected.filter((s) => s !== option.value);
    } else {
      updates = [...selected, option.value];
    }
    onChange({ [name]: updates } as Record<N, T[]>);
  };

  const getOptions = () => {
    return searchByLabel(options, search);
  };

  return (
    <div style={style} className={className}>
      <div className={classes.titleWrapper}>
        <Title size="xs">{title}</Title>
        <Button variant="ghost" onClick={() => toggleSelectAll()}>
          {selected.length === options.length ? 'Clear' : 'Select all'}
        </Button>
      </div>
      {filter && (
        <UnderlineTextField
          showCloseIcon
          className={classes.search}
          placeholder="Filter..."
          value={search}
          onChange={setSearch}
        />
      )}
      <div>
        <List className={classes.list}>
          {getOptions().map((option) => (
            <ListItem
              key={`${option.value}`}
              className={classes.listItem}
              onClick={() => handleSelectOption(option)}>
              <CheckBox
                checked={selected.includes(option.value)}
                className={classes.checkbox}
                edge="start"
                color="orange-3"
              />
              <ListItemText primary={option.label} />
            </ListItem>
          ))}
        </List>
      </div>
    </div>
  );
}
